import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useSignUp } from '@clerk/clerk-react';
import { Alert, Col, Form, Input, Row, Typography } from 'antd';

import { Button } from '@/components/Button';
import { PasswordStrength } from '@/components/PasswordStrength';
import { ROUTES } from '@/routes';
import { parseClerkError } from '@/utils';
import styles from './Register.module.scss';

interface RegisterFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

// Clerk appends `__clerk_status` to invitation-redirect URLs. `SIGN_UP` means the
// ticket is usable to register; any other value (e.g. `expired`) means it isn't.
const CLERK_TICKET_STATUS = {
  SIGN_UP: 'sign_up',
} as const;

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, setActive, isLoaded } = useSignUp();
  const [form] = Form.useForm<RegisterFormValues>();
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ticket = searchParams.get('__clerk_ticket');
  const ticketStatus = searchParams.get('__clerk_status');

  useEffect(() => {
    if (!isLoaded || !signUp || !ticket) return;

    // Clerk appends `__clerk_status` to invitation redirects; anything other than
    // `sign_up` (e.g. `expired`) means the ticket can't be used to register.
    if (ticketStatus && ticketStatus !== CLERK_TICKET_STATUS.SIGN_UP) {
      setError('This invitation link is no longer valid. Please request a new one.');
      return;
    }

    signUp
      .create({ strategy: 'ticket', ticket })
      .then((result) => {
        form.setFieldsValue({
          email: result.emailAddress ?? '',
          firstName: result.firstName ?? '',
          lastName: result.lastName ?? '',
        });
      })
      .catch((err) => setError(parseClerkError(err)));
  }, [isLoaded, ticket]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (values: RegisterFormValues) => {
    if (!signUp || !isLoaded) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await signUp.update({
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate(ROUTES.SESSIONS, { replace: true });
      } else {
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        navigate(ROUTES.VERIFY_DEVICE);
      }
    } catch (err) {
      setError(parseClerkError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Accounts are invite-only (admin invite or package purchase), and both routes
  // arrive here with a Clerk invitation ticket. Without one there is nothing to
  // register against.
  if (!ticket) return <Navigate to={ROUTES.LOGIN} replace />;

  return (
    <div className={styles.container}>
      <Typography.Title level={1} className={styles.title}>
        Create your account
      </Typography.Title>
      <Typography.Text className={styles.subtitle}>
        Already have an account?{' '}
        <Link to={ROUTES.LOGIN} className={styles.subtitleLink}>
          Login
        </Link>
      </Typography.Text>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className={styles.alert}
        />
      )}

      <Form<RegisterFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark={false}
        colon={false}
        className={styles.form}
        initialValues={{ firstName: '', lastName: '', email: '', password: '' }}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="firstName"
              label="First name"
              rules={[{ required: true, message: 'Please enter your first name' }]}
            >
              <Input
                placeholder="Alex"
                size="large"
                autoComplete="given-name"
                disabled={isSubmitting}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="lastName"
              label="Last name"
              rules={[{ required: true, message: 'Please enter your last name' }]}
            >
              <Input
                placeholder="Johnson"
                size="large"
                autoComplete="family-name"
                disabled={isSubmitting}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="email"
          label="Email address"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input
            placeholder="alex@example.com"
            size="large"
            autoComplete="email"
            disabled
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[{ required: true, message: 'Please create a password' }]}
        >
          <Input.Password
            placeholder="At least 8 characters"
            size="large"
            autoComplete="new-password"
            disabled={isSubmitting}
            onChange={(e) => setPassword(e.target.value)}
            iconRender={(visible) => (visible ? 'Hide' : 'Show')}
          />
        </Form.Item>
        <PasswordStrength value={password} />

        <Button
          appearance="solid"
          htmlType="submit"
          size="large"
          block
          loading={isSubmitting}
          disabled={!isLoaded}
        >
          Sign up
        </Button>
      </Form>
    </div>
  );
};

export default Register;
